/* eslint-disable */
import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import Select from "react-select";
import { useTranslation } from "react-i18next";

/* Import API and configuration utilities */
import { getData, postData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";
import { reactSelectStyle } from "Config/Config";

/**
 * @typedef {Object} SaveFeedbackRulePopupProps
 * @property {string} [moduleslug=""] - The slug of the module this feedback rule belongs to.
 * @property {function(): void} [afterSaveData=() => {}] - Callback function to execute after a successful save operation.
 * @property {string|null} [feedbackRuleId=null] - The ID of the feedback rule if in edit mode, otherwise null for create mode.
 * @property {function(string|null): void} [setFeedbackRuleId=() => {}] - Setter for the feedbackRuleId state in the parent component.
 * @property {function(boolean): void} [setShowAlert=() => {}] - Setter for showing/hiding alerts in the parent component.
 * @property {function(string): void} [setAlertMessage=() => {}] - Setter for the alert message in the parent component.
 * @property {function(string): void} [setMessageType=() => {}] - Setter for the alert message type (e.g., "success", "error") in the parent component.
 */

/**
 * SaveFeedbackRulePopup Component
 *
 * This component provides a form within an offcanvas popup for creating or editing feedback rules.
 * It handles form validation, API calls for fetching roles and surveys, and saving/updating feedback rules.
 *
 * @param {SaveFeedbackRulePopupProps} props - The properties for the component.
 * @returns {JSX.Element} The SaveFeedbackRulePopup component.
 */
const SaveFeedbackRulePopup = ({
  moduleslug = "",
  afterSaveData = () => {},
  feedbackRuleId = null,
  setFeedbackRuleId = () => {},
  setShowAlert = () => {},
  setAlertMessage = () => {},
  setMessageType = () => {},
}) => {
  /* --- Hooks and Context --- */
  const token = localStorage.getItem("token");
  const { t } = useTranslation();
  const params = useParams();

  /* --- State Management --- */

  /**
   * @property {string} senderRoleInput - Input string for filtering sender roles.
   * @property {Array<Object>} senderRoleOptions - Options displayed in the sender role select dropdown.
   * @property {string} receiverRoleInput - Input string for filtering receiver roles.
   * @property {Array<Object>} receiverRoleOptions - Options displayed in the receiver role select dropdown.
   * @property {string} surveyInput - Input string for filtering surveys.
   * @property {Array<Object>} surveyOptions - Options displayed in the survey select dropdown.
   */
  const [senderRoleInput, setSenderRoleInput] = useState("");
  const [senderRoleOptions, setSenderRoleOptions] = useState([]);
  const [receiverRoleInput, setReceiverRoleInput] = useState("");
  const [receiverRoleOptions, setReceiverRoleOptions] = useState([]);
  const [surveyInput, setSurveyInput] = useState("");
  const [surveyOptions, setSurveyOptions] = useState([]);

  /**
   * @property {string} title - The title of the feedback rule.
   * @property {string} slug - The unique slug/identifier for the feedback rule.
   * @property {Object|null} senderRoleValue - The selected sender role object for the React-Select component.
   * @property {string|null} senderRoleId - The ID of the selected sender role.
   * @property {Object|null} receiverRoleValue - The selected receiver role object for the React-Select component.
   * @property {string|null} receiverRoleId - The ID of the selected receiver role.
   * @property {Object|null} surveyValue - The selected survey object for the React-Select component.
   * @property {string|null} surveyId - The ID of the selected survey.
   * @property {Object} errors - Object to store validation error messages for form fields.
   * @property {boolean} isSaving - Indicates if the form is currently in the process of saving data.
   */
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [senderRoleValue, setSenderRoleValue] = useState(null);
  const [senderRoleId, setSenderRoleId] = useState(null);
  const [receiverRoleValue, setReceiverRoleValue] = useState(null);
  const [receiverRoleId, setReceiverRoleId] = useState(null);
  const [surveyValue, setSurveyValue] = useState(null);
  const [surveyId, setSurveyId] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  /* --- Form Validation --- */

  /**
   * Validates the form fields before submission.
   * @returns {boolean} True if the form is valid, false otherwise.
   */
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = t("Please provide a title for this feedback rule.");
    }

    if (surveyId === null) {
      newErrors.survey = t("Please select the survey that should be sent.");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [title, surveyId, t]);

  /* --- API Calls --- */

  /**
   * Fetches roles (sender/receiver) from the API based on input.
   * @param {string} input - The filter string for role names.
   * @param {function(Array<Object>): void} setOptions - State setter for role options.
   * @returns {Promise<void>}
   */
  const getRoles = useCallback(
    async (input, setOptions) => {
      try {
        const requestUrl = `${url.API_BASE_URL}${url.API_GET_ALL_MODULE_ROLE}?token=${token}&slug=MOD_EVENT&filtername=${input}`;
        const response = await getData(requestUrl);
        if (response.status) {
          setOptions(response.data);
        } else {
          console.error("Failed to fetch roles:", response.message);
          setAlertMessage(t("Failed to load roles. Please try again."));
          setMessageType("error");
          setShowAlert(true);
        }
      } catch (error) {
        console.error("Error fetching roles:", error.message);
        setAlertMessage(t("An error occurred while fetching roles."));
        setMessageType("error");
        setShowAlert(true);
      }
    },
    [token, setAlertMessage, setMessageType, setShowAlert, t]
  );

  /**
   * Fetches surveys from the API based on input.
   * @param {string} input - The filter string for survey names.
   * @returns {Promise<void>}
   */
  const getSurveys = useCallback(
    async (input) => {
      try {
        const requestUrl = `${url.API_BASE_URL}${url.API_GET_ALL_SURVEY}?token=${token}&moduleslug=MOD_EVENT&filtername=${input}`;
        const response = await getData(requestUrl);

        if (response.status) {
          const apiData = response.data.map((item) => ({
            value: item._id,
            label: item.name,
          }));
          setSurveyOptions(apiData);
        } else {
          console.error("Failed to fetch surveys:", response.message);
          setAlertMessage(t("Failed to load surveys. Please try again."));
          setMessageType("error");
          setShowAlert(true);
        }
      } catch (error) {
        console.error("Error fetching surveys:", error.message);
        setAlertMessage(t("An error occurred while fetching surveys."));
        setMessageType("error");
        setShowAlert(true);
      }
    },
    [token, setAlertMessage, setMessageType, setShowAlert, t]
  );

  /**
   * Fetches details of an existing feedback rule for editing.
   * @returns {Promise<void>}
   */
  const getFeedbackRuleDetails = useCallback(async () => {
    if (!feedbackRuleId) return; // Ensure feedbackRuleId exists for fetching details
    try {
      const requestUrl = `${url.API_BASE_URL}${url.API_GET_FEEDBACK_RULE_DETAILS}/${feedbackRuleId}?token=${token}`;
      const response = await getData(requestUrl);

      if (response.status && response.data) {
        const { title, slug, senderrole, recieverrole, surveyid } =
          response.data;
        setTitle(title || "");
        setSlug(slug || "");

        if (senderrole) {
          setSenderRoleValue({ value: senderrole._id, label: senderrole.name });
          setSenderRoleId(senderrole._id);
        } else {
          setSenderRoleValue(null);
          setSenderRoleId(null);
        }

        if (recieverrole) {
          setReceiverRoleValue({
            value: recieverrole._id,
            label: recieverrole.name,
          });
          setReceiverRoleId(recieverrole._id);
        } else {
          setReceiverRoleValue(null);
          setReceiverRoleId(null);
        }

        if (surveyid) {
          setSurveyValue({ value: surveyid._id, label: surveyid.name });
          setSurveyId(surveyid._id);
        } else {
          setSurveyValue(null);
          setSurveyId(null);
        }
      } else {
        console.error(
          "Failed to fetch feedback rule details:",
          response.message
        );
        setAlertMessage(t("Failed to load feedback rule details."));
        setMessageType("error");
        setShowAlert(true);
      }
    } catch (error) {
      console.error("Error fetching feedback rule details:", error.message);
      setAlertMessage(
        t("An error occurred while fetching feedback rule details.")
      );
      setMessageType("error");
      setShowAlert(true);
    }
  }, [feedbackRuleId, token, setAlertMessage, setMessageType, setShowAlert, t]);

  /* --- Handlers for Select Component Changes --- */

  /**
   * Handles change for sender role selection.
   * @param {Object|null} val - The selected option object or null if cleared.
   */
  const senderRoleChangeHandler = useCallback((val) => {
    setSenderRoleValue(val);
    setSenderRoleId(val ? val.value : null);
  }, []);

  /**
   * Handles change for receiver role selection.
   * @param {Object|null} val - The selected option object or null if cleared.
   */
  const receiverRoleChangeHandler = useCallback((val) => {
    setReceiverRoleValue(val);
    setReceiverRoleId(val ? val.value : null);
  }, []);

  /**
   * Handles change for survey selection.
   * @param {Object|null} val - The selected option object or null if cleared.
   */
  const surveyChangeHandler = useCallback((val) => {
    setSurveyValue(val);
    setSurveyId(val ? val.value : null);
  }, []);

  /* --- Save and Reset Logic --- */

  /**
   * Handles the saving or updating of a feedback rule.
   * Performs validation and API call.
   * @returns {Promise<void>}
   */
  const saveHandler = useCallback(async () => {
    if (!validateForm()) {
      setAlertMessage(
        t("Please correct the highlighted fields before saving.")
      );
      setMessageType("error");
      setShowAlert(true);
      return;
    }

    setIsSaving(true);
    try {
      const sendingData = {
        title,
        slug,
        senderrole: senderRoleId,
        recieverrole: receiverRoleId,
        surveyid: surveyId,
        eventid: params.id,
      };

      let requestUrlEndpoint;
      let response;

      if (feedbackRuleId) {
        requestUrlEndpoint = `${url.API_UPDATE_FEEDBACK_RULE}/${feedbackRuleId}?token=${token}`;
        response = await putData(
          `${url.API_BASE_URL}${requestUrlEndpoint}`,
          sendingData
        );
      } else {
        requestUrlEndpoint = `${url.API_CREATE_FEEDBACK_RULE}?token=${token}`;
        response = await postData(
          `${url.API_BASE_URL}${requestUrlEndpoint}`,
          sendingData
        );
      }

      setIsSaving(false);

      if (response.status) {
        afterSaveData();
        // Ensure bootstrap is available globally or imported
        if (window.bootstrap && window.bootstrap.Offcanvas) {
          const offcanvasElement = document.querySelector("#savefeedbackrule");
          const offcanvasInstance =
            window.bootstrap.Offcanvas.getInstance(offcanvasElement);
          if (offcanvasInstance) {
            offcanvasInstance.hide();
          }
        }
        resetHandler();
        setAlertMessage(t("Feedback rule saved successfully!"));
        setMessageType("success");
        setShowAlert(true);
      } else {
        console.error("Save failed:", response.message);
        setAlertMessage(t(response.message || "Failed to save feedback rule."));
        setMessageType("error");
        setShowAlert(true);
      }
    } catch (error) {
      setIsSaving(false);
      console.error("Error saving feedback rule:", error.message);
      setAlertMessage(t("An error occurred while saving the feedback rule."));
      setMessageType("error");
      setShowAlert(true);
    }
  }, [
    title,
    slug,
    senderRoleId,
    receiverRoleId,
    surveyId,
    params.id,
    feedbackRuleId,
    token,
    afterSaveData,
    validateForm,
    setAlertMessage,
    setMessageType,
    setShowAlert,
    t,
  ]);

  /**
   * Resets all form fields and error states.
   */
  const resetHandler = useCallback(() => {
    setTitle("");
    setSlug("");
    setSenderRoleValue(null);
    setSenderRoleId(null);
    setReceiverRoleValue(null);
    setReceiverRoleId(null);
    setSurveyValue(null);
    setSurveyId(null);
    setFeedbackRuleId(null); // Reset parent's feedbackRuleId
    setErrors({});
    setIsSaving(false); // Ensure saving state is reset
  }, [setFeedbackRuleId]);

  /* --- Effects --- */

  /**
   * Effect to fetch feedback rule details when feedbackRuleId changes (for edit mode).
   */
  useEffect(() => {
    if (feedbackRuleId) {
      getFeedbackRuleDetails();
    } else {
      // If feedbackRuleId becomes null, reset the form for create mode
      resetHandler();
    }
  }, [feedbackRuleId, getFeedbackRuleDetails, resetHandler]);

  /**
   * Effect to fetch sender roles when senderRoleInput changes.
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      if (senderRoleInput.length > 0) {
        getRoles(senderRoleInput, setSenderRoleOptions);
      } else {
        setSenderRoleOptions([]);
      }
    }, 300); // Debounce input for better performance
    return () => clearTimeout(handler);
  }, [senderRoleInput, getRoles]);

  /**
   * Effect to fetch receiver roles when receiverRoleInput changes.
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      if (receiverRoleInput.length > 0) {
        getRoles(receiverRoleInput, setReceiverRoleOptions);
      } else {
        setReceiverRoleOptions([]);
      }
    }, 300); // Debounce input for better performance
    return () => clearTimeout(handler);
  }, [receiverRoleInput, getRoles]);

  /**
   * Effect to fetch surveys when surveyInput changes.
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      if (surveyInput.length > 0) {
        getSurveys(surveyInput);
      } else {
        setSurveyOptions([]);
      }
    }, 300); // Debounce input for better performance
    return () => clearTimeout(handler);
  }, [surveyInput, getSurveys]);

  /* --- Custom Styles --- */

  /**
   * Custom styles for the Survey Select component, including error highlighting.
   */
  const customStyleForSurvey = {
    control: (base) => ({
      ...base,
      borderColor: errors.survey ? "#dc3545" : "#ced4da",
      "&:hover": {
        borderColor: errors.survey ? "#dc3545" : "#ced4da",
      },
      borderRadius: "7px",
      padding: "3px",
      minHeight: "40px",
      boxShadow: "none",
    }),
    // Add styles for dropdown indicator and clear indicator for better icon integration
    dropdownIndicator: (base) => ({
      ...base,
      padding: 0,
      paddingRight: "8px",
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: 0,
      paddingLeft: "8px",
    }),
  };

  /* --- Render Method --- */

  return (
    <div
      className="offcanvas md offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="savefeedbackrule"
      aria-labelledby="savefeedbackrule"
    >
      {/* Header design similar to ViewResponsePopup */}
      <div className="offcanvas-header bg-primary text-white p-4 pe-5 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              rule
            </span>{" "}
            {/* Using 'rule' icon as a placeholder */}
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="offcanvasLabelDetailsLabel"
            >
              {feedbackRuleId
                ? t("Edit Feedback Rule")
                : t("Create Feedback Rule")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {feedbackRuleId
                ? t("Manage existing feedback rule")
                : t("Define new feedback rule")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        ></button>
      </div>
      <div className="offcanvas-body p-4">
        {/* Professional Card Layout */}
        <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
          <form
            onSubmit={(e) => e.preventDefault()}
            className="d-flex flex-column"
          >
            <div className="fields-container flex-fill">
              {/* Title */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="ruleTitle"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Rule Title")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="ruleTitle"
                  className={`form-control fs-sm shadow-none ${
                    errors.title ? "is-invalid" : "border border-gray-300"
                  }`}
                  placeholder={t("e.g., Speaker Feedback for Attendees")}
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    if (errors.title) {
                      setErrors((prev) => ({ ...prev, title: "" }));
                    }
                  }}
                  aria-describedby="titleFeedback"
                />
                {errors.title && (
                  <div id="titleFeedback" className="invalid-feedback">
                    {errors.title}
                  </div>
                )}
              </div>

              {/* Slug */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="ruleSlug"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Slug (Unique Identifier)")}
                </label>
                <input
                  type="text"
                  id="ruleSlug"
                  className="form-control fs-sm shadow-none border border-gray-300"
                  placeholder={t(
                    "e.g., speaker-attendee-feedback (auto-generated if empty)"
                  )}
                  value={slug}
                  onChange={(e) => {
                    setSlug(e.target.value);
                  }}
                />
              </div>

              {/* Sender Role */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="senderRole"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Who is giving the feedback? (Sender)")}
                </label>
                <Select
                  placeholder={t("Type a role, e.g., Attendee")}
                  styles={reactSelectStyle("3px", "6px", true, false)}
                  isClearable
                  options={senderRoleOptions}
                  value={senderRoleValue}
                  onChange={senderRoleChangeHandler}
                  onInputChange={setSenderRoleInput}
                  isLoading={
                    senderRoleInput.length > 0 && senderRoleOptions.length === 0
                  }
                  noOptionsMessage={() =>
                    senderRoleInput.length > 0
                      ? t("No roles found.")
                      : t("Start typing to search roles.")
                  }
                />
              </div>

              {/* Receiver Role */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="receiverRole"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Who is receiving the feedback? (Receiver)")}
                </label>
                <Select
                  placeholder={t("Type a role, e.g., Speaker")}
                  styles={reactSelectStyle("3px", "6px", true, false)}
                  isClearable
                  options={receiverRoleOptions}
                  value={receiverRoleValue}
                  onChange={receiverRoleChangeHandler}
                  onInputChange={setReceiverRoleInput}
                  isLoading={
                    receiverRoleInput.length > 0 &&
                    receiverRoleOptions.length === 0
                  }
                  noOptionsMessage={() =>
                    receiverRoleInput.length > 0
                      ? t("No roles found.")
                      : t("Start typing to search roles.")
                  }
                />
              </div>

              {/* Survey */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="survey"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Which survey form should be used?")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <Select
                  placeholder={t("Type a survey, e.g., Speaker Feedback")}
                  styles={customStyleForSurvey}
                  isClearable
                  options={surveyOptions}
                  value={surveyValue}
                  onChange={(val) => {
                    surveyChangeHandler(val);
                    if (errors.survey) {
                      setErrors((prev) => ({ ...prev, survey: "" }));
                    }
                  }}
                  onInputChange={(val) => {
                    setSurveyInput(val);
                    if (errors.survey) {
                      setErrors((prev) => ({ ...prev, survey: "" }));
                    }
                  }}
                  isLoading={
                    surveyInput.length > 0 && surveyOptions.length === 0
                  }
                  noOptionsMessage={() =>
                    surveyInput.length > 0
                      ? t("No surveys found.")
                      : t("Start typing to search surveys.")
                  }
                  aria-describedby="surveyFeedback"
                />
                {errors.survey && (
                  <div id="surveyFeedback" className="invalid-feedback d-block">
                    {errors.survey}
                  </div>
                )}
              </div>
            </div>
            <div className="action d-flex justify-content-end pt-3 border-top">
              <button
                onClick={saveHandler}
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                disabled={isSaving}
                style={{
                  cursor: isSaving ? "not-allowed" : "pointer",
                }}
              >
                {isSaving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    {t("Saving...")}
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined me-1">save</span>{" "}
                    {t("Save Rule")}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SaveFeedbackRulePopup;
